import { useState, useEffect, useCallback } from 'react';

const RECENT_TE_IDS_KEY = 'recentlySelectedTEIds';
const MAX_RECENT_TE_IDS = 5;

interface TEIdData {
  TE_ID: string;
  account_name: string;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country_name?: string;
  standards?: string;
  [key: string]: any;
}

export const useRecentTEIds = () => {
  const [recentTEIds, setRecentTEIds] = useState<TEIdData[]>([]);

  // Get recently selected TE IDs from localStorage
  const getRecentlySelectedTEIds = useCallback((): TEIdData[] => {
    try {
      const stored = localStorage.getItem(RECENT_TE_IDS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading recent TE IDs from localStorage', error);
      return [];
    }
  }, []);

  // Save a TE ID to recently selected list
  const saveRecentlySelectedTEId = useCallback((teData: TEIdData) => {
    try {
      // Validate that required fields are not empty
      if (!teData.TE_ID?.trim() || !teData.account_name?.trim()) {
        return;
      }

      const recentTEIds = getRecentlySelectedTEIds();
      // Remove if already exists (to avoid duplicates)
      const filtered = recentTEIds.filter(
        (te: TEIdData) => te.TE_ID !== teData.TE_ID
      );
      // Add new one to the beginning and keep only top 5
      const updated = [teData, ...filtered].slice(0, MAX_RECENT_TE_IDS);
      localStorage.setItem(RECENT_TE_IDS_KEY, JSON.stringify(updated));
      // Update state
      setRecentTEIds(updated);
    } catch (error) {
      console.error('Error saving recent TE ID to localStorage', error);
    }
  }, [getRecentlySelectedTEIds]);

  // Clear all recent TE IDs
  const clearRecentTEIds = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_TE_IDS_KEY);
      setRecentTEIds([]);
    } catch (error) {
      console.error('Error clearing recent TE IDs from localStorage', error);
    }
  }, []);

  // Load recently selected TE IDs on hook mount
  useEffect(() => {
    const recent = getRecentlySelectedTEIds();
    setRecentTEIds(recent);
  }, [getRecentlySelectedTEIds]);

  return {
    recentTEIds,
    saveRecentlySelectedTEId,
    clearRecentTEIds,
    getRecentlySelectedTEIds,
  };
};
